import { Api } from "../../Api";
import { DataFrame, Method, Status } from "../../DataFrame";
import { downloadStringAsCsv, ParseObjToMap } from "../../Helpers";
import { DevicesService } from "../Devices/DevicesService";
import { DeviceResponseType, IDevice } from "../Devices/IDevice";
import { IDevicesService } from "../Devices/IDevicesService";
import { EnergyMeasurementParameter } from "../Devices/IDeviceState";
import { ExalusConnectionService } from "../ExalusConnectionService";
import { ResponseResult } from "../FieldChangeResult";
import { IExalusConnectionService } from "../IExalusConnectionService";
import { IUpdatesProvider } from "../Updates/IUpdatesProvider";
import { UpdatesProvider } from "../Updates/UpdatesProvider";
import { IStatesHistoryService, StateHistoryErrorCode } from "./IStatesHistoryService";
import { AggregateDataType, AvailableState, AveragingStateValues, BrightnessHistory, DataRangeType, EnergyHistory, IAvailableStatePerChannel, StateData, StateDataResponse, StateInterval } from "./StatesHistory";

export class StatesHistoryService implements IStatesHistoryService {
    public static readonly ServiceName: string = "StatesHistoryService";
    private DELIM: string = ";";
    private _connection: IExalusConnectionService | null = null;

    constructor() {
        this._connection = Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName);
    }


    numToCsv(val: number | string): string {
        const n = typeof val === "string" ? Number(val.replace(",", ".")) : val;

        if (!Number.isFinite(n))
            return "";

        return n.toLocaleString("en-US", { useGrouping: false });
    }

    quoteIfNeeded(field: string): string {
        return field.includes(this.DELIM) || field.includes('"')
            ? `"${field.replace(/"/g, '""')}"`
            : field;
    }

    ExportPressureToCsv(device: IDevice, channel: number, data: StateDataResponse<AveragingStateValues>): void {
        downloadStringAsCsv(this.ExportAveragingStateValuesToCsv(device, channel, data), `PressureHistory_${device.Name}_${channel}.csv`);
    }

    ExportBrightnessToCsv(device: IDevice, channel: number, data: StateDataResponse<BrightnessHistory>): void {
        if (!data?.Data?.length) return;

        const lines: string[] = [];
        // ---------- header ----------
        const first = data.Data[0];
        const header: string[] = ["Time", "AggregatedBy"];

        header.push(
            `LuxValue`,
            `LuxToLinearScale`,
            `LightCondition`
        );

        lines.push(header.join(this.DELIM));

        // ---------- data rows ----------
        data.Data.forEach(el => {
            const row: string[] = [
                this.quoteIfNeeded(el.Time),
                this.quoteIfNeeded(el.AggregatedBy)
            ];

            if (el.Values !== null) {
                row.push(this.numToCsv(el.Values?.LuxValue));
                row.push(this.numToCsv(el.Values?.LuxToLinearScale));
                row.push(this.numToCsv(el.Values?.LightCondition));
            }

            lines.push(row.join(this.DELIM));
        });

        downloadStringAsCsv(lines.join("\n"), `BrightnessHistory_${device.Name}_${channel}.csv`);
    }

    ExportHumidityToCsv(device: IDevice, channel: number, data: StateDataResponse<AveragingStateValues>): void {
        downloadStringAsCsv(this.ExportAveragingStateValuesToCsv(device, channel, data), `HumidityHistory_${device.Name}_${channel}.csv`);
    }

    ExportWindSpeedToCsv(device: IDevice, channel: number, data: StateDataResponse<AveragingStateValues>): void {
        downloadStringAsCsv(this.ExportAveragingStateValuesToCsv(device, channel, data), `WindSpeedHistory_${device.Name}_${channel}.csv`);
    }

    ExportTemperatureToCsv(device: IDevice, channel: number, data: StateDataResponse<AveragingStateValues>): void {
        downloadStringAsCsv(this.ExportAveragingStateValuesToCsv(device, channel, data), `TemperatureHistory_${device.Name}_${channel}.csv`);
    }

    ExportAveragingStateValuesToCsv(device: IDevice, channel: number, data: StateDataResponse<AveragingStateValues>): string {
        if (!data?.Data?.length) return "";

        const lines: string[] = [];
        // ---------- header ----------
        const first = data.Data[0];
        const header: string[] = ["Time", "AggregatedBy"];

        header.push(
            `LastValue`,
            `Average`,
            `Mode`,
            `Min`,
            `Max`
        );

        lines.push(header.join(this.DELIM));

        // ---------- data rows ----------
        data.Data.forEach(el => {
            const row: string[] = [
                this.quoteIfNeeded(el.Time),
                this.quoteIfNeeded(el.AggregatedBy)
            ];

            if (el.Values !== null) {
                row.push(this.numToCsv(el.Values?.LastValue));
                row.push(this.numToCsv(el.Values?.Average));
                row.push(this.numToCsv(el.Values?.Mode));
                row.push(this.numToCsv(el.Values?.Min));
                row.push(this.numToCsv(el.Values?.Max));
            }

            lines.push(row.join(this.DELIM));
        });

        return lines.join("\n");
    }

    ExportEnergyToCsv(device: IDevice, channel: number, response: StateDataResponse<EnergyHistory>): void {
        let data = response.Data;

        if (!data?.length) return;

        const lines: string[] = [];
        // ---------- header ----------
        const first = data[0];
        const header: string[] = ["Time", "AggregatedBy"];

        first.Values?.MeasurementAveragingParameters.forEach((_v, key) => {
            header.push(
                `${key}:LastValue`,
                `${key}:Average`,
                `${key}:Mode`,
                `${key}:Min`,
                `${key}:Max`
            );
        });
        first.Values?.MeasurementNonAveragingParmeters.forEach((_v, key) =>
            header.push(`${key}`)
        );

        lines.push(header.join(this.DELIM));

        // ---------- data rows ----------
        data.forEach(el => {
            const row: string[] = [
                this.quoteIfNeeded(el.Time),
                this.quoteIfNeeded(el.AggregatedBy)
            ];

            el.Values?.MeasurementAveragingParameters.forEach(vals => {
                row.push(
                    this.numToCsv(vals.LastValue),
                    this.numToCsv(vals.Average),
                    this.numToCsv(vals.Mode),
                    this.numToCsv(vals.Min),
                    this.numToCsv(vals.Max)
                );
            });

            el.Values?.MeasurementNonAveragingParmeters.forEach(v =>
                row.push(this.numToCsv(v))
            );

            lines.push(row.join(this.DELIM));
        });

        downloadStringAsCsv(lines.join("\n"), `EnergyHistory_${device.Name}_${channel}.csv`);
    }

    GetStatesByTimeRangeAsync<T>(deviceGuid: string, channel: number, responseType: DeviceResponseType, timeRange: DataRangeType, time: Date, limit: number, offset: number, orderByDesc?: boolean): Promise<StateDataResponse<T> | ResponseResult<StateHistoryErrorCode>>;
    GetStatesByTimeRangeAsync<T>(dev: IDevice, channel: number, responseType: DeviceResponseType, timeRange: DataRangeType, time: Date, limit: number, offset: number, orderByDesc?: boolean): Promise<StateDataResponse<T> | ResponseResult<StateHistoryErrorCode>>;
    async GetStatesByTimeRangeAsync<T>(dev: any, channel: number, responseType: DeviceResponseType, timeRange: DataRangeType, time: Date, limit: number, offset: number, orderByDesc = false): Promise<StateDataResponse<T> | ResponseResult<StateHistoryErrorCode>> {
        try {
            if (!(await this.DoesSupportPreciseTimeFramesAsync()))
                return new ResponseResult(StateHistoryErrorCode.FunctionalityNotSupported, `State history data in time range is not supported with this version of controller software, update software to get this functionality.`);

            let device: IDevice;

            if (typeof dev === 'string') {
                const result = await Api.Get<IDevicesService>(DevicesService.ServiceName).GetDevice(dev);

                if (result != null)
                    device = result;
                else
                    return new ResponseResult(StateHistoryErrorCode.CannotFindDevice, `Device with specified GUID ${dev} does not exist.`);
            } else {
                device = dev as IDevice;
            }

            if (!device.Channels.any(ch => ch.Number === channel))
                return new ResponseResult(StateHistoryErrorCode.InvalidChannelNumber, `Given channel not found in device with guid: ${device.Guid}.`);

            if (!device.Channels.any(ch => ch.Number === channel && ch.AvailableResponseTypes.any(r => r.Type === responseType)))
                return new ResponseResult(StateHistoryErrorCode.ResponseTypeNotSupported, `Device or channel not supporting requested DeviceResponseType.`);

            // Prepare request object
            const request = new StateInTimeRangeRequest();
            request.DeviceGuid = device.Guid;
            request.DeviceChannel = channel;
            request.StateInterfaceType = responseType;
            request.ReverseOrder = orderByDesc;
            request.DataRange = timeRange;
            request.Time = time;
            request.Limit = limit;
            request.Offset = offset;

            // Optionally, you could use the 'time' parameter for more precise requests if supported

            const result = await this._connection?.SendAndWaitForResponseAsync<any>(new GetStatesIntimeRangeRequest(request), 30000, false);

            if (result == null)
                return new ResponseResult(StateHistoryErrorCode.OtherError, `Cannot get state history data - response is null.`);

            switch (result.Status) {
                case Status.WrongData:
                    switch (result.Data) {
                        case "IncorrectLimitValue":
                            return new ResponseResult(StateHistoryErrorCode.IncorrectLimitValue, `Cannot get state history data - limit value is incorrect!`);
                        case "IncorrectOffsetValue":
                            return new ResponseResult(StateHistoryErrorCode.IncorrectOffsetValue, `Cannot get state history data - offset value is incorrect!`);
                        case "IncorrectArguments":
                            return new ResponseResult(StateHistoryErrorCode.OtherError, `Cannot get state history data - some parameters are incorrect!`);
                        case "UnsupportedRange":
                            return new ResponseResult(StateHistoryErrorCode.OtherError, `Cannot get state history data - time range is incorrect!`);
                        default:
                            return new ResponseResult(StateHistoryErrorCode.OtherError, `Cannot get state history data - unknown error!`);
                    }
                case Status.FatalError:
                    return new ResponseResult(StateHistoryErrorCode.FatalError, `Cannot get state history data - an exception occurred in the controller while reading data!`);
                case Status.ResourceDoesNotExists:
                    return new ResponseResult(StateHistoryErrorCode.ResponseTypeNotSupported, `Device or channel not supporting requested DeviceResponseType or device/channel does not exist. ${result.Data}`);
                case Status.OK:
                    if (result.Data == null)
                        return new ResponseResult(StateHistoryErrorCode.NoData, `Controller responded with status OK, but response does not contain data!`);

                    (result.Data as StateDataResponse<any>).AggregateDataList = (result.Data as StateDataResponse<any>).AggregateDataList.map(d => {
                        d.AggregateData = ParseObjToMap<AggregateDataType, any>(d.AggregateData);
                        return d;
                    });

                    switch (responseType) {
                        case DeviceResponseType.MeasuredEnergy:
                            const parseData = (result.Data as StateDataResponse<any>).Data.map(d => {
                                const result = d as StateData<any>;
                                result.Values.MeasurementAveragingParameters = ParseObjToMap<EnergyMeasurementParameter, AveragingStateValues>(d.Values.MeasurementAveragingParameters);
                                result.Values.MeasurementNonAveragingParmeters = ParseObjToMap<EnergyMeasurementParameter, number>(d.Values.MeasurementNonAveragingParmeters);
                                return result as unknown as StateData<EnergyHistory>;
                            });
                            (result.Data as StateDataResponse<EnergyHistory>).Data = parseData;
                            return result.Data as StateDataResponse<T>;
                        default:
                            return result.Data as StateDataResponse<T>;
                    }
                default:
                    return new ResponseResult(StateHistoryErrorCode.OtherError, `Cannot get state history data - controller responded with response code ${result.Status}`);
            }
        } catch (error) {
            return new ResponseResult(StateHistoryErrorCode.FatalError, `Cannot get state history data - exception occurs! ${error}`);
        }
    }

    GetServiceName(): string {
        return StatesHistoryService.ServiceName;
    }

    async DoesSupportPreciseTimeFramesAsync(): Promise<boolean> {
        let ver = (await Api.Get<IUpdatesProvider>(UpdatesProvider.ServiceName).GetSoftwareRuntimeInfoAsync()).SoftwareVersion.split(".");
        return Number(ver[0]) > 6 || (Number(ver[0]) === 6 && Number(ver[1]) >= 101);
    }

    GetStatesByIntervalAsync<T>(device: IDevice, channel: number, responseType: DeviceResponseType, stateFrom: StateInterval, limit: number, offset: number, orderByDesc?: boolean): Promise<StateDataResponse<T> | ResponseResult<StateHistoryErrorCode>>;
    GetStatesByIntervalAsync<T>(deviceGuid: string, channel: number, responseType: DeviceResponseType, stateFrom: StateInterval, limit: number, offset: number, orderByDesc?: boolean): Promise<StateDataResponse<T> | ResponseResult<StateHistoryErrorCode>>;
    async GetStatesByIntervalAsync<T>(dev: unknown, channel: number, responseType: DeviceResponseType, stateFrom: StateInterval, limit: number, offset: number, orderByDesc = false): Promise<StateDataResponse<T> | ResponseResult<StateHistoryErrorCode>> {
        try {

            if (!(await this.IsFunctionalitySupportedAsync()))
                return new ResponseResult(StateHistoryErrorCode.FunctionalityNotSupported, `State history data is not supported with this version of controller software, update software to get this functionality.`);

            let device: IDevice;

            if (typeof dev == 'string') {
                const result = await Api.Get<IDevicesService>(DevicesService.ServiceName).GetDevice(dev);

                if (result != null)
                    device = result;
                else
                    return new ResponseResult(StateHistoryErrorCode.CannotFindDevice, `Device with specified GUID ${dev} does not exists.`);
            }
            else {
                device = dev as IDevice;
            }

            if (!device.Channels.any(ch => ch.Number == channel))
                return new ResponseResult(StateHistoryErrorCode.InvalidChannelNumber, `Given channel not found in device with guid: ${device.Guid}.`);

            if (!device.Channels.any(ch => ch.Number == channel && ch.AvailableResponseTypes.any(r => r.Type == responseType)))
                return new ResponseResult(StateHistoryErrorCode.ResponseTypeNotSupported, `Device or channel not supporting requested DeviceResponseType.`);

            const request = new StateDataRequest();
            request.DeviceGuid = device.Guid;
            request.DeviceChannel = channel;
            request.StateInterfaceType = responseType;
            request.ReverseOrder = orderByDesc;
            request.Limit = limit;
            request.Offset = offset;

            let ver = (await Api.Get<IUpdatesProvider>(UpdatesProvider.ServiceName).GetSoftwareRuntimeInfoAsync()).SoftwareVersion.split(".");
            let isSupported = Number(ver[0]) > 6 || (Number(ver[0]) === 6 && Number(ver[1]) >= 101);

            if (isSupported) {
                switch (stateFrom) {
                    case StateInterval.ThisHour:
                        request.Range = StateInterval.Hour;
                        break;
                    case StateInterval.ThisDay:
                        request.Range = StateInterval.Day;
                        break;
                    case StateInterval.ThisWeek:
                        request.Range = StateInterval.Week;
                        break;
                    case StateInterval.ThisMonth:
                        request.Range = StateInterval.Month;
                        break;
                    case StateInterval.ThisYear:
                        request.Range = StateInterval.Year;
                        break;
                }
            }
            else
                request.Range = stateFrom;


            const result = await this._connection?.SendAndWaitForResponseAsync<any>(new GetLastStatesRequest(request), 30000, false);

            if (result == null)
                return new ResponseResult(StateHistoryErrorCode.OtherError, `Cannot get state history data - response is null.`);

            switch (result.Status) {
                case Status.WrongData:
                    switch (result.Data) {
                        case "IncorrectLimitValue":
                            return new ResponseResult(StateHistoryErrorCode.IncorrectLimitValue, `Cannot get state history data - limit value is incorrect!`);
                        case "IncorrectOffsetValue":
                            return new ResponseResult(StateHistoryErrorCode.IncorrectOffsetValue, `Cannot get state history data - offset value is incorrect!`);
                        case "IncorrectArguments":
                            return new ResponseResult(StateHistoryErrorCode.OtherError, `Cannot get state history data - some parameters are incorrect!`);
                        default:
                            return new ResponseResult(StateHistoryErrorCode.OtherError, `Cannot get state history data - unknown error!`);
                    }
                case Status.FatalError:
                    return new ResponseResult(StateHistoryErrorCode.FatalError, `Cannot get state history data - an exception occurred in the controller while reading data!`);
                case Status.ResourceDoesNotExists:
                    return new ResponseResult(StateHistoryErrorCode.ResponseTypeNotSupported, `Device or channel not supporting requested DeviceResponseType.`);
                case Status.OK:
                    if (result.Data == null)
                        return new ResponseResult(StateHistoryErrorCode.NoData, `Controller responede with status OK, but response does not contain data!`);

                    (result.Data as StateDataResponse<any>).AggregateDataList = (result.Data as StateDataResponse<any>).AggregateDataList.map(d => {
                        d.AggregateData = ParseObjToMap<AggregateDataType, any>(d.AggregateData);
                        return d;
                    });

                    switch (responseType) {
                        case DeviceResponseType.MeasuredEnergy:
                            const parseData = (result.Data as StateDataResponse<any>).Data.map(d => {
                                const result = d as StateData<any>;
                                result.Values.MeasurementAveragingParameters = ParseObjToMap<EnergyMeasurementParameter, AveragingStateValues>(d.Values.MeasurementAveragingParameters);
                                result.Values.MeasurementNonAveragingParmeters = ParseObjToMap<EnergyMeasurementParameter, number>(d.Values.MeasurementNonAveragingParmeters);

                                return result as unknown as StateData<EnergyHistory>;
                            });

                            (result.Data as StateDataResponse<EnergyHistory>).Data = parseData;
                            return result.Data as StateDataResponse<T>;
                        default:
                            return result.Data as StateDataResponse<T>;
                    }

                default:
                    return new ResponseResult(StateHistoryErrorCode.OtherError, `Cannot get state history data - controller responded with response code ${result.Status}`);
            }
        }
        catch (error) {
            return new ResponseResult(StateHistoryErrorCode.FatalError, `Cannot get state history data - exeption occurs! ${error}`);
        }
    }

    async GetAvailableStatesAsync(): Promise<ResponseResult<StateHistoryErrorCode> | AvailableState[]> {
        try {
            const result = await this._connection?.SendAndWaitForResponseAsync<AvailableStateResponse[]>(new GetAvailableStatesRequest(), 12000, false);

            if (result == null)
                return new ResponseResult(StateHistoryErrorCode.OtherError, `Cannot get available states - response is null.`);

            if (result.Status != Status.OK)
                return new ResponseResult(StateHistoryErrorCode.OtherError, `Cannot get available states - controller responded with response code ${result.Status}.`);

            if (result.Data == null)
                return new ResponseResult(StateHistoryErrorCode.NoData, `Cannot get available states - controller responede with status OK, but response does not contain data!`);

            return result.Data.map(r => {
                const x = new AvailableState();
                x.StateInterfaceType = r.StateInterfaceType;
                x.StateObjectType = r.StateObjectType;
                return x;
            })
        }
        catch (error) {
            return new ResponseResult(StateHistoryErrorCode.FatalError, `Cannot get available states - exeption occurs! ${error}`);
        }
    }

    async IsFunctionalitySupportedAsync(): Promise<boolean> {
        let ver = (await Api.Get<IUpdatesProvider>(UpdatesProvider.ServiceName).GetSoftwareRuntimeInfoAsync()).SoftwareVersion.split(".");
        return Number(ver[0]) > 3 || (Number(ver[0]) === 3 && Number(ver[1]) >= 42);
    }

    GetAvailableStatesPerChannelAsync(device: IDevice, channel: number): Promise<IAvailableStatePerChannel | ResponseResult<StateHistoryErrorCode>>;
    GetAvailableStatesPerChannelAsync(deviceGuid: string, channel: number): Promise<IAvailableStatePerChannel | ResponseResult<StateHistoryErrorCode>>;
    async GetAvailableStatesPerChannelAsync(dev: unknown, channel: number): Promise<IAvailableStatePerChannel | ResponseResult<StateHistoryErrorCode>> {
        let device: IDevice;

        if (typeof dev == 'string') {
            const result = await Api.Get<IDevicesService>(DevicesService.ServiceName).GetDevice(dev);

            if (result != null)
                device = result;
            else
                return new ResponseResult(StateHistoryErrorCode.CannotFindDevice, `Device with specified GUID ${dev} does not exists.`);
        }
        else {
            device = dev as IDevice;
        }

        const result = await this._connection?.SendAndWaitForResponseAsync<AvailableStatePerChannelResponse>(new GetAvailableStatesPerChannelRequest(new ChannelParamRequest(device.Guid, channel)), 8000, false);

        if (result == null)
            return new ResponseResult(StateHistoryErrorCode.OtherError, `Cannot get available states - response is null.`);

        switch (result.Status) {
            case Status.ResourceDoesNotExists: {
                switch ((result.Data as unknown as string)) {
                    case "DeviceNotFound":
                        return new ResponseResult(StateHistoryErrorCode.CannotFindDevice, `Cannot get available states - device does not exists!`);
                    case "ChannelNotFound":
                        return new ResponseResult(StateHistoryErrorCode.InvalidChannelNumber, `Cannot get available states - wrong channel!`);
                    default:
                        return new ResponseResult(StateHistoryErrorCode.OtherError, `Cannot get available states - unknown error!`);
                }
            }
            case Status.OK: {
                if (result.Data == null)
                    return new ResponseResult(StateHistoryErrorCode.NoData, `Cannot get available states - controller responede with status OK, but response does not contain data!`);

                const remappedStates = result.Data.AvailableStates.map(r => {
                    const x = new AvailableState();
                    x.StateInterfaceType = r.StateInterfaceType;
                    x.StateObjectType = r.StateObjectType;
                    return x;
                })

                result.Data.AvailableStates = remappedStates
                return result.Data as IAvailableStatePerChannel;
            }
            default:
                return new ResponseResult(StateHistoryErrorCode.OtherError, `Cannot get available states - controller responded with response code ${result.Status}`);
        }
    }
}

class GetLastStatesRequest extends DataFrame<StateDataRequest> {
    constructor(data: StateDataRequest) {
        super();
        this.Resource = "/statehistory/states/get/last";
        this.Method = Method.Get;
        this.Data = data;
    }
}

class StateDataRequest {
    DeviceGuid: string = "";
    DeviceChannel: number = 0;
    StateInterfaceType: string = "";
    Range: StateInterval = StateInterval.Day;
    ReverseOrder: boolean = false;
    Limit: number = 0;
    Offset: number = 0;
}

class GetStatesIntimeRangeRequest extends DataFrame<StateInTimeRangeRequest> {
    constructor(data: StateInTimeRangeRequest) {
        super();
        this.Resource = "/statehistory/states/get/timerange";
        this.Method = Method.Get;
        this.Data = data;
    }
}

class StateInTimeRangeRequest {
    DeviceGuid: string = "";
    DeviceChannel: number = 0;
    StateInterfaceType: string = "";
    DataRange: DataRangeType = DataRangeType.Day;
    Time: Date = new Date();
    ReverseOrder: boolean = false;
    Limit: number = 0;
    Offset: number = 0;
}

class GetAvailableStatesRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/statehistory/states/available";
        this.Method = Method.Get;
    }
}

class GetAvailableStatesPerChannelRequest extends DataFrame<ChannelParamRequest> {
    constructor(request: ChannelParamRequest) {
        super();
        this.Resource = "/statehistory/states/available/per/channel";
        this.Method = Method.Get;
        this.Data = request
    }
}

class ChannelParamRequest {
    DeviceGuid: string;
    Channel: number;
    constructor(guid: string, channel: number) {
        this.DeviceGuid = guid;
        this.Channel = channel
    }
}

class AvailableStateResponse {
    StateInterfaceType: string = "";
    StateObjectType: string = "";
}

class AvailableStatePerChannelResponse {
    DeviceGuid: string = "";
    Channel: number = 0;
    AvailableStates: AvailableStateResponse[] = []
}